interface EditorialDividerProps {
  /**
   * Optional numeral or label rendered between the rules.
   * - Pass a number (e.g. 3) and it is auto-converted to a Roman numeral ("III").
   * - Pass a string (e.g. "I", "VII", "Notes") and it is rendered verbatim (uppercased).
   */
  numeral?: string | number;
  /** Symbol prefix shown before the numeral. Defaults to the section sign. */
  symbol?: string;
  /** Optional spacing/utility overrides. Does not affect internal typography. */
  className?: string;
}

const ROMAN_MAP: Array<[number, string]> = [
  [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
  [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
  [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
];

const toRoman = (num: number): string => {
  if (!Number.isFinite(num) || num <= 0) return "";
  let n = Math.floor(num);
  let result = "";
  for (const [val, sym] of ROMAN_MAP) {
    while (n >= val) {
      result += sym;
      n -= val;
    }
  }
  return result;
};

const normalizeLabel = (value: string | number): string => {
  if (typeof value === "number") return toRoman(value);
  return value.trim().toUpperCase();
};

/**
 * Editorial divider — two thin accent rules with an optional § N numeral between.
 * Typography and spacing are standardized regardless of label length so all
 * dividers across the site share identical rhythm.
 */
const EditorialDivider = ({ numeral, symbol = "§", className }: EditorialDividerProps) => {
  const base = `editorial-divider ${className ?? ""}`.trim();

  if (numeral === undefined || numeral === null || numeral === "") {
    return <div className={base} role="separator" aria-hidden />;
  }

  const label = normalizeLabel(numeral);

  return (
    <div className={base} data-numeral={label} role="separator" aria-hidden>
      <span className="divider-mark">
        {symbol} {label}
      </span>
    </div>
  );
};

export const romanNumeral = toRoman;
export default EditorialDivider;
